import { API_BASE as BASE_URL, getAuthHeaders, baseHeaders, apiFetch } from './api-fetch';
import { API, buildPath } from './api-endpoints';

export interface EconomicIndicators {
  interestRate: number; // Prime interest rate (%)
  repoRate: number; // SARB repo rate (%)
  inflationRate: number; // CPI inflation rate (%)
  gdpGrowth: number; // GDP growth rate (%)
  unemploymentRate: number; // Unemployment rate (%)
  exchangeRateUSD: number; // ZAR to USD
  propertyGrowthRate: number; // Annual property price growth (%)
  rentalYield: number; // Average rental yield (%)
}

export interface CalculatorDefaults {
  loanTerm: number; // Default loan term in years
  depositPercentage: number; // Default deposit percentage
  transferDutyRate: number; // Transfer duty max rate (%)
  bondRegistrationFee: number; // Fixed bond registration fee (ZAR)
  deedsOfficeFee: number; // Fixed deeds office fee (ZAR)
  vatRate: number; // VAT rate e.g. 0.155 for 15.5%
  bankInitFee: number; // Bank initiation fee incl. VAT (NCA cap)
  bondDeedsFee: number; // Bond deeds office fee (fixed v1.0)
  transferDeedsFee: number; // Transfer deeds office fee (fixed v1.0)
  postFicaFee: number; // Post/petties/FICA fee incl. VAT (per side)
}

export interface PlatformSettings {
  maintenanceMode: boolean;
  allowNewRegistrations: boolean;
  maxFileUploadSize: number; // In bytes
  sessionTimeout: number; // In milliseconds
}

export interface Settings {
  economicIndicators: EconomicIndicators;
  calculatorDefaults: CalculatorDefaults;
  platformSettings: PlatformSettings;
  lastUpdated: string;
  updatedBy: string;
}

export const settingsAPI = {
  /**
   * Get all platform settings
   */
  async getSettings(): Promise<{ settings: Settings }> {
    try {
      const response = await apiFetch(`${BASE_URL}${API.admin.getSettings.path}`, {
        method: 'GET',
        headers: baseHeaders,
      });

      if (!response.ok) {
        const error = await response.json();
        const msg = typeof error.error === 'string' ? error.error : error.error?.message || 'Failed to fetch settings';
        throw new Error(msg);
      }

      const data = await response.json();
      // Backend returns { success, data: { settings: [{ key, value, updatedAt }] } }
      const settingsArray: any[] = data.data?.settings ?? data.settings ?? [];
      const find = (key: string) => {
        const raw = settingsArray.find((s: any) => s.key === key)?.value;
        if (!raw) return {};
        if (typeof raw === 'string') {
          try { return JSON.parse(raw); } catch { return {}; }
        }
        return raw;
      };
      const latest = settingsArray.reduce((acc: any, s: any) =>
        !acc || (s.updatedAt && s.updatedAt > acc) ? s.updatedAt : acc, '');
      return {
        settings: {
          economicIndicators: find('economic-indicators'),
          calculatorDefaults: find('calculator-defaults'),
          platformSettings: find('platform-settings'),
          lastUpdated: latest ?? '',
          updatedBy: settingsArray.find((s: any) => s.updatedBy)?.updatedBy ?? '',
        }
      };
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },

  /**
   * Update all platform settings
   */
  async updateSettings(settings: Partial<Settings>, updatedBy: string): Promise<{ success: boolean; settings: Settings }> {
    try {
      const response = await apiFetch(`${BASE_URL}${API.admin.getSettings.path}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ value: settings })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update settings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  /**
   * Update economic indicators only
   */
  async updateEconomicIndicators(indicators: Partial<EconomicIndicators>, _updatedBy: string): Promise<{ success: boolean; economicIndicators: EconomicIndicators }> {
    try {
      const response = await apiFetch(`${BASE_URL}/admin/settings/economic-indicators`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ value: indicators })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update economic indicators');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating economic indicators:', error);
      throw error;
    }
  },

  /**
   * Update calculator defaults only
   */
  async updateCalculatorDefaults(defaults: Partial<CalculatorDefaults>, _updatedBy: string): Promise<{ success: boolean; calculatorDefaults: CalculatorDefaults }> {
    try {
      const response = await apiFetch(`${BASE_URL}/admin/settings/calculator-defaults`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ value: defaults })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update calculator defaults');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating calculator defaults:', error);
      throw error;
    }
  },

  /**
   * Reset all settings to defaults
   */
  async resetSettings(updatedBy: string): Promise<{ success: boolean; settings: Settings }> {
    try {
      const response = await apiFetch(`${BASE_URL}/admin/settings/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ updatedBy })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reset settings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error resetting settings:', error);
      throw error;
    }
  }
};
